import axios from 'axios';
import { IS_TESTNET, TONCENTER_API_KEY, TONCENTER_V3_API_ENDPOINT } from '~/config.js';
import { hexToBase64 } from '~/utils.js';
import { canonizeAddress } from '~/tonweb';
import { getJettonPricesDyor } from '~/api/dyor';
import opCodesDictionary from '../../json/traceLabels.json';
import store from '../store.js';
// import opCodesDictionary from '../../json/traceLabels.json';

/* eslint camelcase: "off", func-names: "off" */

// Disable headers if api key is not set. Otherwise
// axios will make a pre-flight request:
const httpHeaders = TONCENTER_API_KEY
    ? { 'X-API-Key': TONCENTER_API_KEY }
    : undefined;

const http = axios.create({
    baseURL: TONCENTER_V3_API_ENDPOINT,
    headers: httpHeaders,
});

// New actions from Toncenter
const supported_action_types = [
    'multisig_create_order',
    'multisig_approve',
    'multisig_execute',
    'jvault_stake',
    'jvault_unstake',
    'jvault_claim',
    'jvault_unstake_request',
    'vesting_send_message',
    'vesting_add_whitelist',
    'evaa_supply',
    'evaa_withdraw',
    'evaa_liquidate',
];

/**
 * This function returns last blocks sorted by time.
 *
 * @param  {Numer} options.wc
 * @param  {Number} options.limit
 * @param  {Number} options.offset
 * @param  {Number} options.startUtime
 * @param  {Number} options.endUtime
 * @return {Promise<Array>}
 */
export const getPreviousBlocks = async function ({ wc, limit, offset, startUtime, endUtime, afterLt } = {}) {
    const { data: { blocks: result } } = await http.get('/blocks', {
        params: {
            offset, limit,
            after_lt: afterLt,
            workchain: wc,
            start_utime: startUtime,
            end_utime: endUtime,
            sort: 'desc',
        },
    });

    result.forEach((block) => {
        /* eslint no-param-reassign: "off" */
        block.root_hash_hex = block.root_hash;
        block.root_hash = hexToBase64(block.root_hash);
    });

    return result.map(Object.freeze);
};

export const getBlockHeader = async function ({ workchain, shard, seqno }) {
    const query = { workchain, shard, seqno, limit: 1 };

    const { data: { blocks } } = await http.get('/blocks', { params: query });

    return blocks[0];
};

const getSourceAndDestination = function (msg, address, hash, addressBook) {
    const from = msg.source ? addressBook[msg.source].user_friendly : null;
    const to = msg.destination ? addressBook[msg.destination].user_friendly : null;

    return {
        from, to,
        is_out: address === from,
        amount: msg.value || null,
        created_at: msg.created_at,
        hash,
    };
};

/**
 * @param  {Number} options.wc
 * @param  {Number} options.startUtime
 * @param  {Number} options.endUtime
 * @return {Promise<Array>}
 */
export const getAllTransactions = async function ({ wc, limit, startUtime, endUtime } = {}) {
    const { data } = await http.get('/transactions', {
        params: {
            limit,
            workchain: wc,
            start_utime: startUtime,
            end_utime: endUtime,
            sort: 'desc',
        },
    });

    const result = data.transactions;
    const addressBook = data.address_book;

    const transactions = result.map((tx) => {
        const address = tx.account;
        const hash = tx.hash;

        const is_service = !tx.in_msg && tx.out_msgs.length === 0;
        const is_external = tx.out_msgs.length === 0 && !tx.in_msg?.source && tx.in_msg?.destination === address;
        let msg = undefined;

        if (is_service) {
            msg = {
                source: address,
                destination: null,
                created_at: tx.created_at || tx.now,
            };
        } else if (tx.out_msgs.length > 0) {
            msg = tx.out_msgs.at(-1);
        } else {
            msg = tx.in_msg;
        }

        const sourceAndDestination = getSourceAndDestination(msg, address, hash, addressBook);

        sourceAndDestination.is_service = is_service;
        sourceAndDestination.is_external = is_external;
        sourceAndDestination.created_at = tx.now;

        return sourceAndDestination;
    });

    return transactions.sort((a, b) => b.created_at - a.created_at).map(Object.freeze);
};

/**
 * @see https://api.toncenter.com/index/#/default/get_top_accounts_by_balance_v1_topAccountsByBalance_get
 * @param  {Number} options.limit
 * @param  {Number} options.offset
 * @return {Promise<Array>}
 */
export const getTopBalances = async function ({ limit, offset } = {}) {
    const { data } = await http.get('/topAccountsByBalance', {
        params: { limit, offset },
    });

    return data.map(item => ({
        ...item,
        account: canonizeAddress(item.account),
    }));
};

/**
 * @param  {String} hash
 * @return {Promise<Object>}
 */
export const getTransactionByHash = async function (hash) {
    const { data } = await http.get('/transactions', { params: { hash } });

    return Object.freeze(data);
};

/**
 * @param  {String} hash
 * @return {Promise<Object>}
 */
export const getTransactionByInMsgHash = async function (hash) {
    const { data } = await http.get('/transactionsByMessage', {
        params: {
            msg_hash: hash,
            direction: 'in',
        },
    });

    return Object.freeze(data);
};

/**
 * @param  {String} hash
 * @return {Promise<Object>}
 */
export const getTransactionByHashOrInMessageHash = async function (hash) {
    const byHash = await getTransactionByHash(hash);

    if (byHash?.transactions?.length > 0) {
        return byHash;
    }

    /* eslint no-return-await: "off" */
    return await getTransactionByInMsgHash(hash);
};

/**
 * @param  {String} domain
 * @return {Promise<String|Boolean>}
 */
export const getAddressByDomain = async function (domain) {
    const cached = {
        'wolf.t.me': 'UQDYzZmfsrGzhObKJUw4gzdeIxEai3jAFbiGKGwxvxHinf4K',
        'nerd.t.me': 'UQDYzZmfsrGzhObKJUw4gzdeIxEai3jAFbiGKGwxvxHinf4K',
        'tolya.ton': 'UQCdqXGvONLwOr3zCNX5FjapflorB6ZsOdcdfLrjsDLt3AF4',
        'get-toncoin.ton': 'EQDshc_H_RAJNi5CG1oBQfUQ1lQBB6tz54Kf2h756Xp14_Cv',
        'tester.ton': 'UQDIwNxS40E81AmRxgygybAaWKajTUm2v3SwNiNrdWtv8Uj8',
    };

    if (cached[domain]) return cached[domain];

    const { data } = await http.get('/dns/records', { params: { domain } });

    const dns = data.records[0];
    const addressBook = data.address_book;

    if (dns) {
        return addressBook[dns.nft_item_owner].user_friendly;
    }

    return false;
};

/**
 * @param  {Number} options.workchain
 * @param  {Number} options.shard
 * @param  {Number} options.seqno
 * @return {Promise<Object>}
 */
export const getBlockTransactions = async function ({ workchain, shard, seqno, offset, limit = 40 }) {
    const query = { workchain, shard, seqno, offset, limit, sort: 'asc' };

    const { data: result } = await http.get('/transactions', { params: query });

    // Convert address hex notation to base64:
    result.transactions.forEach((tx) => {
        tx.account = result.address_book[tx.account].user_friendly; /* eslint no-param-reassign: "off" */
    });

    return result;
};

/**
 * @param  {Number} options.seqno
 * @return {Promise<Object>}
 */
export const getShards = async function ({ seqno }) {
    const { data: { blocks: result } } = await http.get('masterchainBlockShardState', { params: { seqno } });

    const shards = result.filter(block => block.workchain >= 0).reverse();

    return shards;
};

/**
 * @return {Promise<Object>}
 */
export const getLastBlock = async function () {
    const { data: result } = await http.get('masterchainInfo');

    return Object.freeze(result.last);
};

/**
 * This function extracts the data, that may be unique for every msg
 *
 * @param  {Object} msg
 * @return {Object}
 */
const parseMessageData = function extractMessageDetails(msg, addressBook) {
    const from = addressBook[msg.source]?.user_friendly || null;
    const to = addressBook[msg.destination]?.user_friendly || null;

    const message = msg?.message_content?.decoded?.type === 'text_comment' ? msg?.message_content?.decoded?.comment : undefined;

    return { from, to,
        message,
        amount: msg.value,
        op: msg.opcode ? `0x${(msg.opcode >>> 0).toString(16)}` : null, // eslint-disable-line no-bitwise
        is_bounced: msg.bounced === true,
    };
};

/**
 * @return {Promise<Object>}
 */
export const getTransactionsV3 = async function (address, { limit, offset, end_lt }) {
    // Chosen dates from calendar
    const start_utime = store.state.datePickerFilter[0];
    const end_utime = store.state.datePickerFilter[1];

    const { data: result } = await http.get('transactions', {
        params: {
            account: address,
            limit,
            offset,
            end_lt,
            start_utime,
            end_utime,
        },
    });

    const addressBook = result?.address_book;
    const transactions = result?.transactions;

    const groups = [];

    transactions.forEach((tx) => {
        const is_service = !tx.in_msg && tx.out_msgs.length === 0;

        const is_external = tx.out_msgs.length === 0
            && !tx.in_msg?.source
            && addressBook[tx.in_msg?.destination] === address;

        const newWalletTxSuccess = tx.description?.action?.result_code === undefined && tx.description?.compute_ph?.exit_code === undefined;
        const executionSuccess = tx.description.action?.action_result_code !== null && parseInt(tx.description?.action?.result_code, 10) <= 1;

        const is_success = newWalletTxSuccess || executionSuccess;
        const exit_code = is_success
            ? tx.description?.action?.result_code
            : tx.description?.compute_ph?.exit_code;

        const txDetails = {
            address,
            hash: tx.hash,
            fee: tx.total_fees,
            lt: tx.lt,
            timestamp: parseInt(tx.now + '000', 10),
            output_count: tx.out_msgs.length,
            exit_code: exit_code || 0,
            messages: [],
        };

        const msgDetails = {
            is_service, is_external, is_success,
            is_aggregated: false,
        };

        // Don't display long message list (e.g. multisends), show aggregated info instead:
        if (tx.out_msgs.length > 10) {
            const aggregatedAmount = tx.out_msgs.reduce((total, outMsg) => parseInt(outMsg.value, 10) + total, 0);
            txDetails.messages.push({ ...msgDetails,
                amount: aggregatedAmount,
                is_aggregated: true,
                is_bounced: tx.out_msgs.some(msg => msg.bounced),
                from: address,
                to: 'multiple destinations', // must be truthy to indicate that we do have the destination
            });

        // Otherwise push out_msgs to the list in chronological (reverse) order:
        } else {
            tx.out_msgs.reverse().forEach((outMsg) => {
                txDetails.messages.push({ ...msgDetails, ...parseMessageData(outMsg, addressBook) });
            });
        }

        // Then push the input message:
        if (tx.in_msg?.source) {
            txDetails.messages.push({ ...msgDetails, ...parseMessageData(tx.in_msg, addressBook) });
        }

        // Special case when there're neither out_msgs, nor in_msg (like in system contract):
        if (is_service) {
            txDetails.messages.push({ ...msgDetails,
                from: address,
                to: null,
            });
        }

        // Special case for external messages (e. g. when activating wallet):
        if (is_external) {
            txDetails.messages.push({ ...msgDetails,
                from: null,
                to: address,
            });
        }

        for (let i = 0; i < txDetails.messages.length; i += 1) {
            Object.freeze(txDetails.messages[i]);
        }

        groups.push(Object.freeze(txDetails));
    });

    return groups;
};

export const getCodeHash = async function (address) {
    let code_hash = undefined;

    const query = {
        account: address,
        sort: 'asc',
    };

    const { data: result } = await http.get('/transactions', { params: query });

    result?.transactions?.forEach((tx) => {
        if (tx?.account_state_after?.code_hash && !code_hash) {
            code_hash = tx.account_state_after.code_hash;
        }
    });

    return code_hash;
};

export const getWalletInfo = async function (address) {
    const { data: response } = await http.get(`wallet?address=${address}`);

    return response;
};

export const getAccountInfo = async function (address) {
    const { data: response } = await http.get(`account?address=${address}`);

    return response;
};

// Temporary function. After toncenter's API will be proxied on backend the function should be removed and 'getAccountInfo' used instead
export const getAccountInfoSha256 = async function (address) {
    const testnet = IS_TESTNET ? '&testnet=true' : '';
    const { data: response } = await axios.get(`https://jetton-index.tonscan.org/contract/code?address=${address}${testnet}`);

    return response;
};

export const getPackOfBalances = async function (addresses = []) {
    const { data: response } = await http.get('walletStates', {
        params: {
            address: addresses,
        },
    });

    const balances = {};

    response.wallets.forEach((addr) => {
        balances[response.address_book[addr.address].user_friendly] = addr.balance;
    });

    return balances;
};

export const getAccountStates = async function (address) {
    const { data: response } = await http.get(`accountStates?address=${address}&include_boc=false`);

    return response;
};

// eslint-disable-next-line default-param-last
export const generateMessage = function (eventAction, addressBook, msgAccount, metadata = [], finality) {
    let eventName = eventAction.type;
    const eventObj = eventAction.details;

    const messages = {
        from: undefined,
        to: undefined,
        action: undefined,
        event: eventName,
        meta: undefined,
        source_alias: undefined,
        destination_alias: undefined,
        message: eventObj?.encrypted ? null : (eventObj?.comment || null),
        encrypted: eventObj?.encrypted,
        is_external: eventName === 'Unknown',
        is_success: eventAction.success,
        is_swapped: eventName === 'jetton_swap',
        is_aggregated: false,
        is_bounced: false,
        is_service: false,
        op: eventObj?.opcode,
        finality: finality || eventObj?.finality || 'finalized',
    };

    function getAddressbook(address) {
        if (address && addressBook[address]) {
            return addressBook[address].user_friendly;
        }
        return null;
    }

    function getAlias(address) {
        if (address && addressBook[address]) {
            return addressBook[address].domain;
        }
        return undefined;
    }

    function getImgProxy(proxyUrl, fallbackUrl) {
        return proxyUrl || fallbackUrl;
    }

    // Exception - jettons sent to burn address are burned (wow)
    const isBurnAddress = eventObj.receiver === '0:0000000000000000000000000000000000000000000000000000000000000000'
        && eventName === 'jetton_transfer';

    if (isBurnAddress) {
        messages.event = 'jetton_burn';
        eventName = 'jetton_burn';
    }

    switch (eventName) {
        case 'ton_transfer': {
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.destination);
            messages.amount = eventObj.value;
            messages.event = msgAccount === getAddressbook(eventObj.source)
                ? 'sent_ton'
                : 'received_ton';
            break;
        }

        case 'subscribe': {
            messages.from = getAddressbook(eventObj.subscriber);
            messages.to = getAddressbook(eventObj.beneficiary);
            messages.source_alias = getAlias(eventObj.subscriber);
            messages.destination_alias = getAlias(eventObj.beneficiary);
            messages.amount = eventObj.amount;
            break;
        }

        case 'unsubscribe': {
            messages.from = getAddressbook(eventObj.subscriber);
            messages.to = getAddressbook(eventObj.beneficiary);
            messages.source_alias = getAlias(eventObj.subscriber);
            messages.destination_alias = getAlias(eventObj.beneficiary);
            break;
        }

        case 'call_contract': {
            messages.tonAmount = eventObj.value || 0;
            messages.from = getAddressbook(eventObj?.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj.destination);
            messages.amount = eventObj.value;
            break;
        }

        case 'nft_mint': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.meta = Object.freeze({
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: domain && getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : getImgProxy(metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, metadata[eventObj.nft_item]?.token_info[0]?.image), // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
            });
            messages.from = getAddressbook(eventObj?.source);
            messages.to = getAddressbook(eventObj.owner);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj.owner);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.event = 'deploy_nft';
            break;
        }

        case 'nft_transfer': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.from = getAddressbook(eventObj?.old_owner);
            messages.to = getAddressbook(eventObj?.new_owner);
            messages.source_alias = getAlias(eventObj?.old_owner);
            messages.destination_alias = getAlias(eventObj?.new_owner);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: domain && getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : getImgProxy(metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, metadata[eventObj.nft_item]?.token_info[0]?.image), // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                marketplace: eventObj?.marketplace,
            };
            messages.event = 'nft_transfer';
            if (eventObj.is_purchase) {
                messages.event = 'nft_purchase';
                messages.amount = eventObj?.price;
                messages.meta.hideAmount = msgAccount !== getAddressbook(eventObj?.old_owner) && msgAccount !== getAddressbook(eventObj?.new_owner);
            }
            break;
        }

        case 'jetton_transfer': {
            messages.from = getAddressbook(eventObj.sender);
            messages.to = getAddressbook(eventObj.receiver);
            messages.source_alias = getAlias(eventObj.sender);
            messages.destination_alias = getAlias(eventObj.receiver);
            messages.action = Object.freeze({
                type: msgAccount === getAddressbook(eventObj?.sender) ? 'jetton:transfer' : 'jetton:transfer_notification',
                amount: eventObj.amount,
                sender: getAddressbook(eventObj.sender),
                destination: messages.to = getAddressbook(eventObj.receiver),
            });
            messages.meta = Object.freeze({
                symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                jetton_address: getAddressbook(eventObj.asset),
                decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                image: metadata[eventObj?.asset]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
            });
            messages.event = msgAccount === getAddressbook(eventObj?.sender)
                ? 'sent_jetton'
                : 'received_jetton';
            break;
        }

        case 'jetton_mint': {
            messages.from = getAddressbook(eventObj.asset);
            messages.to = getAddressbook(eventObj.receiver);
            messages.source_alias = getAlias(eventObj.asset);
            messages.destination_alias = getAlias(eventObj.receiver);
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                jetton_address: getAddressbook(eventObj.asset),
                decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
            });

            break;
        }

        case 'jetton_burn': {
            messages.from = getAddressbook(eventObj.owner) || getAddressbook(eventObj.sender);
            messages.to = getAddressbook(eventObj.asset);
            messages.source_alias = getAlias(eventObj.owner) || getAddressbook(eventObj.sender);
            messages.destination_alias = getAlias(eventObj.asset);
            messages.amount = eventObj.amount;
            messages.meta = Object.freeze({
                symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                jetton: getAddressbook(eventObj.asset),
                jetton_address: getAddressbook(eventObj.asset),
                decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                image: metadata[eventObj?.asset]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle
            });
            messages.action = Object.freeze({
                type: 'jetton:burn',
                amount: eventObj.amount,
            });
            break;
        }

        case 'jetton_swap': {
            messages.amount_in = eventObj.dex_incoming_transfer.amount;
            messages.amount_out = eventObj.dex_outgoing_transfer.amount;
            messages.dex = eventObj?.dex;
            messages.action = Object.freeze({
                type: 'jetton:swap',
            });
            messages.meta = Object.freeze({
                jetton_in_address: getAddressbook(eventObj.dex_incoming_transfer?.asset),
                amount_in: eventObj.dex_incoming_transfer?.amount,
                symbol_in: metadata[eventObj.dex_incoming_transfer?.asset]?.token_info[0]?.symbol,
                decimals_in: Number(metadata[eventObj.dex_incoming_transfer?.asset]?.token_info[0]?.extra?.decimals),
                image_in: eventObj.dex_incoming_transfer?.asset === null
                    ? 'https://ton.org/icons/custom/ton_logo.svg'
                    : metadata[eventObj.dex_incoming_transfer?.asset]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle

                jetton_out_address: getAddressbook(eventObj.dex_outgoing_transfer?.asset),
                amount_out: eventObj.dex_outgoing_transfer?.amount,
                symbol_out: metadata[eventObj.dex_outgoing_transfer?.asset]?.token_info[0]?.symbol,
                decimals_out: Number(metadata[eventObj.dex_outgoing_transfer?.asset]?.token_info[0]?.extra?.decimals),
                image_out: eventObj.dex_outgoing_transfer?.asset === null
                    ? 'https://ton.org/icons/custom/ton_logo.svg'
                    : metadata[eventObj.dex_outgoing_transfer?.asset]?.token_info[0]?.extra?._image_big, // eslint-disable-line no-underscore-dangle

                dex: eventObj?.dex,
            });
            messages.from = getAddressbook(eventObj.sender);
            messages.to = getAddressbook(eventObj.dex_incoming_transfer.destination);
            messages.source_alias = getAlias(eventObj.sender);
            messages.destination_alias = getAlias(eventObj.dex_incoming_transfer.destination);
            break;
        }

        case 'contract_deploy':
            messages.from = getAddressbook(eventObj?.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj.destination);
            messages.amount = eventObj.value;

            break;

        case 'renew_dns':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.asset);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.asset);
            messages.meta = Object.freeze({
                domain: metadata[eventObj.asset]?.token_info[0]?.extra?.domain,
            });
            break;

        case 'change_dns':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.asset);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.asset);
            messages.meta = Object.freeze({
                domain: metadata[eventObj.asset]?.token_info[0]?.extra?.domain,
            });
            break;

        case 'delete_dns':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.asset);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.asset);
            messages.meta = Object.freeze({
                domain: metadata[eventObj.asset]?.token_info[0]?.extra?.domain,
            });
            break;

        case 'auction_bid': {
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.from = getAddressbook(eventObj.bidder);
            messages.to = getAddressbook(eventObj?.auction || eventObj.nft_item);
            messages.source_alias = getAlias(eventObj.bidder);
            messages.destination_alias = getAlias(eventObj?.auction || eventObj.nft_item);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.amount = eventObj.amount;

            if (eventObj?.nft_item) {
                messages.action = Object.freeze({
                    type: 'nft:transfer_action',
                    nft: getAddressbook(eventObj.nft_item),
                });
            }

            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: domain && getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : getImgProxy(metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, metadata[eventObj.nft_item]?.token_info[0]?.image), // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                symbol: 'TON',
            };

            break;
        }

        case 'election_deposit':
            messages.from = getAddressbook(eventObj.stake_holder);
            messages.to = 'Ef8zMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzM0vF'; // Always elector
            messages.source_alias = getAlias(eventObj.stake_holder);
            messages.destination_alias = getAlias('Ef8zMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzM0vF');
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                provider: eventObj?.provider,
            });
            break;

        case 'election_recover':
            messages.from = 'Ef8zMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzM0vF'; // Always elector
            messages.to = getAddressbook(eventObj.stake_holder);
            messages.source_alias = getAlias('Ef8zMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzM0vF');
            messages.destination_alias = getAlias(eventObj.stake_holder);
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                provider: eventObj?.provider,
            });
            break;

        case 'stake_withdrawal':
            messages.from = getAddressbook(eventObj.pool || eventObj.payout_nft);
            messages.to = getAddressbook(eventObj.stake_holder);
            messages.source_alias = getAlias(eventObj.pool || eventObj.payout_nft);
            messages.destination_alias = getAlias(eventObj.stake_holder);
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                provider: eventObj?.provider,
            });
            break;

        case 'stake_withdrawal_request':
            messages.from = getAddressbook(eventObj.stake_holder);
            messages.to = getAddressbook(eventObj.pool);
            messages.source_alias = getAlias(eventObj.stake_holder);
            messages.destination_alias = getAlias(eventObj.pool);
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                provider: eventObj?.provider,
            });
            break;

        case 'stake_deposit':
            messages.from = getAddressbook(eventObj.stake_holder);
            messages.to = getAddressbook(eventObj.pool);
            messages.source_alias = getAlias(eventObj.stake_holder);
            messages.destination_alias = getAlias(eventObj.pool);
            messages.amount = eventObj.amount;

            messages.meta = Object.freeze({
                provider: eventObj?.provider,
            });
            break;

        case 'dex_withdraw_liquidity':
            messages.from = getAddressbook(eventObj.pool);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.pool);
            messages.destination_alias = getAlias(eventObj.source);
            messages.dex = eventObj.dex;
            messages.details = Object.freeze({
                jetton_1_address: getAddressbook(eventObj.asset_1),
                amount_1: eventObj.amount_1,
                symbol_1: metadata[eventObj.asset_1]?.token_info[0]?.symbol,
                decimals_1: Number(metadata[eventObj.asset_1]?.token_info[0]?.extra?.decimals),
                jetton_2_address: getAddressbook(eventObj.asset_2),
                amount_2: eventObj.amount_2,
                symbol_2: metadata[eventObj.asset_2]?.token_info[0]?.symbol,
                decimals_2: Number(metadata[eventObj.asset_2]?.token_info[0]?.extra?.decimals),
            });
            break;

        case 'dex_deposit_liquidity':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.pool);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.pool);
            messages.dex = eventObj.dex;
            messages.details = Object.freeze({
                jetton_1_address: getAddressbook(eventObj.asset_1),
                amount_1: eventObj.amount_1,
                symbol_1: metadata[eventObj.asset_1]?.token_info[0]?.symbol,
                decimals_1: Number(metadata[eventObj.asset_1]?.token_info[0]?.extra?.decimals),
                jetton_2_address: getAddressbook(eventObj.asset_2),
                amount_2: eventObj.amount_2,
                symbol_2: metadata[eventObj.asset_2]?.token_info[0]?.symbol,
                decimals_2: Number(metadata[eventObj.asset_2]?.token_info[0]?.extra?.decimals),
            });
            break;

        case 'multisig_execute':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.destination);
            break;

        case 'multisig_create_order':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.destination);
            break;

        case 'multisig_approve':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.destination);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.destination);
            break;

        case 'jvault_claim':
            messages.from = getAddressbook(eventObj.pool);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.pool);
            messages.destination_alias = getAlias(eventObj.source);

            messages.action = Object.freeze({
                type: msgAccount === getAddressbook(eventObj?.source) ? 'jetton:transfer' : 'jetton:transfer_notification',
                amount: eventObj.claimed_rewards[0]?.amount,
                sender: getAddressbook(eventObj.pool),
                destination: messages.to = getAddressbook(eventObj.source),
            });
            messages.meta = Object.freeze({
                symbol: metadata[eventObj.claimed_rewards[0]?.jetton]?.token_info[0]?.symbol,
                jetton_address: getAddressbook(eventObj.claimed_rewards[0]?.jetton),
                decimals: Number(metadata[eventObj.claimed_rewards[0]?.jetton]?.token_info[0]?.extra?.decimals),
            });
            break;

        case 'jvault_stake':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.pool);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.pool);

            messages.action = Object.freeze({
                type: msgAccount === getAddressbook(eventObj?.source) ? 'jetton:transfer' : 'jetton:transfer_notification',
                amount: eventObj.amount,
                sender: getAddressbook(eventObj.source),
                destination: messages.to = getAddressbook(eventObj.pool),
            });
            messages.meta = Object.freeze({
                symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                jetton_address: getAddressbook(eventObj.asset),
                decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
            });
            break;

        case 'jvault_unstake_request':
            messages.from = getAddressbook(eventObj.pool);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.pool);
            messages.destination_alias = getAlias(eventObj.source);

            messages.amount = eventObj?.amount;

            if (eventObj?.asset) {
                messages.action = Object.freeze({
                    type: msgAccount === getAddressbook(eventObj?.pool) ? 'jetton:transfer' : 'jetton:transfer_notification',
                    amount: eventObj.amount,
                    sender: getAddressbook(eventObj.pool),
                    destination: messages.to = getAddressbook(eventObj.source),
                });
                messages.meta = Object.freeze({
                    symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                    jetton_address: getAddressbook(eventObj.asset),
                    decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                });
            }
            break;

        case 'jvault_unstake':
            messages.from = getAddressbook(eventObj.pool);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.pool);
            messages.destination_alias = getAlias(eventObj.source);

            messages.amount = eventObj?.amount;

            if (eventObj?.asset) {
                messages.action = Object.freeze({
                    type: msgAccount === getAddressbook(eventObj?.pool) ? 'jetton:transfer' : 'jetton:transfer_notification',
                    amount: eventObj.amount,
                    sender: getAddressbook(eventObj.pool),
                    destination: messages.to = getAddressbook(eventObj.source),
                });
                messages.meta = Object.freeze({
                    symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                    jetton_address: getAddressbook(eventObj.asset),
                    decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                });
            }
            break;

        case 'vesting_add_whitelist':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.vesting);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.vesting);
            break;

        case 'vesting_send_message':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.vesting);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.vesting);

            messages.amount = eventObj?.amount;
            break;

        case 'evaa_supply':
            messages.from = getAddressbook(eventObj.source);
            messages.to = getAddressbook(eventObj.master);
            messages.source_alias = getAlias(eventObj.source);
            messages.destination_alias = getAlias(eventObj.master);

            messages.amount = eventObj.amount;

            messages.action = eventObj.is_ton
                ? {}
                : Object.freeze({
                    type: msgAccount === getAddressbook(eventObj?.source) ? 'jetton:transfer' : 'jetton:transfer_notification',
                    amount: eventObj.amount,
                    sender: getAddressbook(eventObj.source),
                    destination: messages.to = getAddressbook(eventObj.master),
                });
            messages.meta = eventObj.is_ton
                ? undefined
                : Object.freeze({
                    symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                    jetton_address: getAddressbook(eventObj.asset),
                    decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                });
            break;

        case 'evaa_withdraw':
            messages.from = getAddressbook(eventObj.master);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.master);
            messages.destination_alias = getAlias(eventObj.source);

            messages.amount = eventObj.amount;

            messages.action = eventObj.asset === null
                ? {}
                : Object.freeze({
                    type: msgAccount === getAddressbook(eventObj?.source) ? 'jetton:transfer' : 'jetton:transfer_notification',
                    amount: eventObj.amount,
                    sender: getAddressbook(eventObj.master),
                    destination: messages.to = getAddressbook(eventObj.source),
                });
            messages.meta = eventObj.asset === null
                ? undefined
                : Object.freeze({
                    symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                    jetton_address: getAddressbook(eventObj.asset),
                    decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                });
            break;

        case 'evaa_liquidate':
            messages.from = getAddressbook(eventObj.borrower);
            messages.to = getAddressbook(eventObj.source);
            messages.source_alias = getAlias(eventObj.borrower);
            messages.destination_alias = getAlias(eventObj.source);

            messages.amount = eventObj?.amount;

            if (eventObj?.asset) {
                messages.action = Object.freeze({
                    type: msgAccount === getAddressbook(eventObj?.borrower) ? 'jetton:transfer' : 'jetton:transfer_notification',
                    amount: eventObj.amount,
                    sender: getAddressbook(eventObj.borrower),
                    destination: messages.to = getAddressbook(eventObj.source),
                });
                messages.meta = Object.freeze({
                    symbol: metadata[eventObj.asset]?.token_info[0]?.symbol,
                    jetton_address: getAddressbook(eventObj.asset),
                    decimals: Number(metadata[eventObj.asset]?.token_info[0]?.extra?.decimals),
                });
            }
            break;

        case 'tick_tock':
            // console.log(eventObj);
            break;

        // Special NFT actions:
        case 'nft_put_on_sale': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.listing_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.listing_address);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : getImgProxy(metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, metadata[eventObj.nft_item]?.token_info[0]?.image), // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                marketplace: eventObj?.marketplace,
            };
            // messages.event = 'nft_transfer';
            messages.amount = eventObj?.full_price;
            break;
        }

        case 'nft_cancel_sale': {
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.sale_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.sale_address);
            break;
        }

        case 'nft_put_on_auction': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.auction_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.auction_address);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : getImgProxy(metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, metadata[eventObj.nft_item]?.token_info[0]?.image), // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                marketplace: eventObj?.marketplace,
            };
            // messages.event = 'nft_transfer';
            messages.amount = eventObj?.full_price;
            break;
        }

        case 'auction_outbid': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.from = getAddressbook(eventObj?.auction_address);
            messages.to = getAddressbook(eventObj?.bidder);
            messages.source_alias = getAlias(eventObj?.auction_address);
            messages.destination_alias = getAlias(eventObj?.bidder);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : getImgProxy(metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, metadata[eventObj.nft_item]?.token_info[0]?.image), // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                marketplace: eventObj?.marketplace,
            };
            // messages.event = 'nft_transfer';
            messages.amount = eventObj?.amount;
            break;
        }

        case 'teleitem_start_auction': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            // messages.action = Object.freeze({
            //     type: 'nft:transfer_action',
            //     nft: getAddressbook(eventObj.nft_item),
            // });
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.auction_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.auction_address);
            // messages.source_type = 'wallet';
            // messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : getImgProxy(metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, metadata[eventObj.nft_item]?.token_info[0]?.image), // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                marketplace: eventObj?.marketplace,
            };
            messages.amount = eventObj?.min_bid;
            break;
        }

        case 'teleitem_cancel_auction': {
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.auction_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.auction_address);
            break;
        }

        case 'nft_purchase': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.from = getAddressbook(eventObj?.old_owner);
            messages.to = getAddressbook(eventObj?.new_owner);
            messages.source_alias = getAlias(eventObj?.old_owner);
            messages.destination_alias = getAlias(eventObj?.new_owner);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: domain && getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : getImgProxy(metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, metadata[eventObj.nft_item]?.token_info[0]?.image), // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                marketplace: eventObj?.marketplace,
            };
            messages.amount = eventObj?.price;
            messages.meta.hideAmount = msgAccount !== getAddressbook(eventObj?.old_owner) && msgAccount !== getAddressbook(eventObj?.new_owner);
            break;
        }

        case 'nft_update_sale': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.from = getAddressbook(eventObj?.source);
            messages.to = getAddressbook(eventObj?.sale_contract);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj?.sale_contract);
            messages.amount = eventObj?.full_price;
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : getImgProxy(metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, metadata[eventObj.nft_item]?.token_info[0]?.image), // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                marketplace: eventObj?.marketplace,
            };
            break;
        }

        case 'dns_purchase': {
            // Getting the domain in case it's a DNS NFT
            const domain = metadata[eventObj.nft_item]?.token_info[0]?.extra?.domain;

            messages.action = Object.freeze({
                type: 'nft:transfer_action',
                nft: getAddressbook(eventObj.nft_item),
            });
            messages.from = getAddressbook(eventObj?.new_owner);
            // messages.to = getAddressbook(eventObj?.new_owner);
            messages.source_alias = getAlias(eventObj?.new_owner);
            // messages.destination_alias = getAlias(eventObj?.new_owner);
            messages.source_type = 'wallet';
            messages.destination_type = 'wallet';
            messages.meta = {
                name: metadata[eventObj.nft_item]?.token_info[0]?.name || domain,
                image: domain && getAddressbook(eventObj.nft_collection) === 'EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz'
                    ? `https://dns-image.mytonwallet.org/img?d=${domain.replace('.ton', '')}`
                    : getImgProxy(metadata[eventObj.nft_item]?.token_info[0]?.extra?._image_big, metadata[eventObj.nft_item]?.token_info[0]?.image), // eslint-disable-line no-underscore-dangle
                collection: metadata[eventObj.nft_collection]?.token_info[0]?.name,
                marketplace: eventObj?.marketplace,
            };
            messages.amount = eventObj?.price;
            messages.meta.hideAmount = msgAccount !== getAddressbook(eventObj?.old_owner) && msgAccount !== getAddressbook(eventObj?.new_owner);
            break;
        }

        case 'dns_release': {
            messages.from = getAddressbook(eventObj?.source);
            messages.to = getAddressbook(eventObj?.auction_address);
            messages.source_alias = getAlias(eventObj?.source);
            messages.destination_alias = getAlias(eventObj?.auction_address);
            messages.amount = eventObj?.value;
            break;
        }

        case 'nft_finish_auction':
        case 'nft_cancel_auction': {
            messages.from = getAddressbook(eventObj?.owner);
            messages.to = getAddressbook(eventObj?.auction_address);
            messages.source_alias = getAlias(eventObj?.owner);
            messages.destination_alias = getAlias(eventObj?.auction_address);
            messages.meta = {
                marketplace: eventObj?.marketplace,
            };
            break;
        }

        default:
            // For test only:
            // In the case if we have an event that was not found previously - we can print it in alert

            // console.log('Found an unknown event: ' + eventName, eventObj);

            // Return empty object so tx table won't be broken
            return {};
    }

    return messages;
};

export const getToncenterActions = async function (account, { tx_hash, msg_hash, trace_id, start_lt, end_lt, limit, offset }, filter) {
    // Chosen dates from calendar
    const start_utime = store.state.datePickerFilter[0];
    const end_utime = store.state.datePickerFilter[1];

    const params = {
        account,
        tx_hash,
        msg_hash,
        trace_id,
        start_utime,
        end_utime,
        limit,
        offset,
        start_lt,
        end_lt,
        sort: 'desc',
        supported_action_types,
        exclude_action_type: [],
    };

    // Excluding filter action groups
    const actionGroups = store.state.actionGroups;
    const excludeList = store.state.excludeGroups.flatMap(item => actionGroups[item]);

    params.exclude_action_type = excludeList;

    // Adding filter for technical actions
    if (filter) {
        // params.exclude_action_type.push('contract_deploy');
        // params.exclude_action_type.push('call_contract');
    }

    const { data: response } = await http.get('/actions', {
        params,
        paramsSerializer: { indexes: null },
    });

    const actions = [];

    response.actions.forEach((action) => {
        const findByHash = actions.findIndex(({ hash }) => hash === action.trace_id);

        const msg = generateMessage(action, response.address_book, account, response.metadata);

        if (msg.event === 'dex_withdraw_liquidity' || msg.event === 'dex_deposit_liquidity') {
            const meta_1 = {
                symbol: msg.details.symbol_1,
                jetton_address: msg.details.jetton_1_address,
                decimals: msg.details.decimals_1,
                amount: msg.details.amount_1,
            };
            const meta_2 = {
                symbol: msg.details.symbol_2,
                jetton_address: msg.details.jetton_2_address,
                decimals: msg.details.decimals_2,
                amount: msg.details.amount_2,
            };
            const action_1 = {
                action: {
                    type: 'dex:deposit_liquidity_asset_1',
                },
            };
            const action_2 = {
                action: {
                    type: 'dex:deposit_liquidity_asset_2',
                },
            };
            actions.push({
                address: account,
                fee: null,
                hash: action.trace_id,
                lt: action.end_lt,
                trace_end_lt: action.trace_end_lt,
                timestamp: action.end_utime * 1000,
                messages: [{ ...msg, ...action_1, meta: meta_1 }, { ...msg, ...action_2, meta: meta_2 }],
                action: 'ok',
            });
            return;
        }

        if (findByHash === -1) {
            actions.push({
                address: account,
                fee: null,
                hash: action.trace_id,
                lt: action.end_lt,
                trace_end_lt: action.trace_end_lt,
                timestamp: action.end_utime * 1000,
                messages: [msg],
                action: 'ok',
            });
        } else {
            actions[findByHash].messages.push(msg);
        }
    });

    return actions;

    // return response.actions.map(action => Object.freeze({
    //     address: account,
    //     fee: null,
    //     hash: action.trace_id,
    //     lt: action.end_lt,
    //     timestamp: action.end_utime * 1000,
    //     messages: [generateMessage(action, response.address_book, account, response.metadata)],
    //     action: 'ok',
    // }));
};

export const getPendingActions = async function (account) {
    const { data: response } = await http.get('/pendingActions', {
        params: {
            account,
        },
        headers: httpHeaders,
    });

    if (!response?.actions) return [];

    const actions = [];

    response.actions.forEach((action) => {
        const findByHash = actions.findIndex(({ hash }) => hash === action.trace_external_hash_norm);

        const msg = generateMessage(action, response.address_book, account, response.metadata, 'pending');

        if (msg.event === 'dex_withdraw_liquidity' || msg.event === 'dex_deposit_liquidity') {
            const meta_1 = {
                symbol: msg.details.symbol_1,
                jetton_address: msg.details.jetton_1_address,
                decimals: msg.details.decimals_1,
                amount: msg.details.amount_1,
            };
            const meta_2 = {
                symbol: msg.details.symbol_2,
                jetton_address: msg.details.jetton_2_address,
                decimals: msg.details.decimals_2,
                amount: msg.details.amount_2,
            };
            const action_1 = {
                action: {
                    type: 'dex:deposit_liquidity_asset_1',
                },
            };
            const action_2 = {
                action: {
                    type: 'dex:deposit_liquidity_asset_2',
                },
            };
            actions.push({
                address: account,
                fee: null,
                hash: action.trace_external_hash_norm || action.trace_external_hash || action.trace_id,
                external_hash: action.trace_external_hash_norm,
                lt: action.end_lt,
                timestamp: action.end_utime * 1000,
                messages: [{ ...msg, ...action_1, meta: meta_1 }, { ...msg, ...action_2, meta: meta_2 }],
                action: 'ok',
                finality: 'pending',
            });
            return;
        }

        if (findByHash === -1) {
            actions.push({
                address: account,
                fee: null,
                hash: action.trace_external_hash_norm || action.trace_external_hash || action.trace_id,
                external_hash: action.trace_external_hash_norm,
                lt: action.end_lt,
                timestamp: action.end_utime * 1000,
                messages: [msg],
                action: 'ok',
                finality: 'pending',
            });
        } else {
            actions[findByHash].messages.push(msg);
        }
    });

    return actions;
};

export const getNFTActions = async function (account, limit, offset) {
    const params = {
        account,
        limit,
        offset,
        include_accounts: false,
        sort: 'desc',
        action_type: [
            'nft_put_on_sale', 'nft_put_on_auction', 'nft_cancel_sale', 'nft_purchase',
            'nft_cancel_auction', 'nft_finish_auction', 'teleitem_start_auction', 'teleitem_cancel_auction',
            'nft_update_sale', 'dns_purchase', 'dns_release', 'nft_transfer', 'nft_mint',
        ],
        supported_action_types: [
            'nft.v3',
        ],
        exclude_action_type: [
            'auction_outbid',
        ],
    };

    const { data: response } = await http.get('/actions', {
        params,
        paramsSerializer: { indexes: null },
    });

    const actions = [];

    response.actions.forEach((action) => {
        const msg = generateMessage(action, response.address_book, account, response.metadata);

        actions.push({
            hash: action.trace_id,
            timestamp: action.end_utime * 1000,
            message: msg,
        });
    });

    return actions;
};

export const getNftByAddress = async function (address) {
    const { data: response } = await http.get('nft/items', {
        params: {
            address,
        },
    });

    return response;
};

export const getNFTsByOwnerAddress = async function (address, limit = 12, offset = 0) {
    const { data: response } = await http.get('nft/items', {
        params: {
            owner_address: address,
            sort_by_last_transaction_lt: true,
            limit,
            offset,
        },
    });

    return response;
};

export const getNFTsByCollectionAddress = async function (address, limit = 12, offset = 0) {
    const { data: response } = await http.get('nft/items', {
        params: {
            collection_address: address,
            sort_by_last_transaction_lt: true,
            limit,
            offset,
        },
    });

    return response;
};

export const getNFTCollectionByAddress = async function (address) {
    const { data: response } = await http.get('nft/collections', {
        params: {
            collection_address: address,
        },
    });

    return response;
};

export const getJettonsByOwnerAddress = async function (address, limit = 20, offset = 0, sortTopJettons = false) {
    const order_list = [
        '0:B113A994B5024A16719F69139328EB759596C38A25F59028B146FECDC3621DFE', // USDT
        '0:AA0BA121449FEDA569E02B12FA755D24E834A7454AECF4649590B6DF742AAC8F', // STAKED
        '0:B8EF4F77A17E5785BD31BA4DA50ABD91852F2B8FEBEE97AD6EE16D941F939198', // GRAM
        '0:7C3B4249FA1A9E0C0A830B5386EB33D805FA55F90CF03DE77492971B20B5EC98', // GEM
        '0:cd0efe78bff4c4539b76eab17293296c74f42cbf99ec499687fefec94893ed32', // TONNEL
        '0:8554D9516FE3471C341FBF743DC6F0D704B2656B2F39E7FC5112CAB96435FA28', // MY
        '0:2F956143C461769579BAEF2E32CC2D7BC18283F40D20BB03E432CD603AC33FFC', // NOT
        '0:AE3E6D351E576276E439E7168117FD64696FD6014CB90C77B2F2CBAACD4FCC00', // MAJOR
        '0:AFC49CB8786F21C87045B19EDE78FC6B46C51048513F8E9A6D44060199C1BF0C', // DOGS
    ].reverse();

    const { data: response } = await http.get('/jetton/wallets', {
        params: {
            owner_address: address,
            jetton_address: sortTopJettons ? order_list : undefined, // Get list of top jettons
            exclude_zero_balance: true,
            limit,
            offset,
        },
    });

    if (response?.jetton_wallets) {
        response.jetton_wallets.sort((b, a) => order_list.indexOf(a.jetton) - order_list.indexOf(b.jetton));
    }

    // Getting jetton prices from DYOR
    const jettonAddressList = [];
    response?.jetton_wallets.forEach((wallet) => {
        jettonAddressList.push(wallet.jetton);
    });

    const prices = await getJettonPricesDyor(jettonAddressList);

    response?.jetton_wallets.forEach((wallet) => {
        const getPrice = prices.filter(p => p.address === wallet.jetton.toLowerCase())[0]?.price || 0;
        const balance = wallet.balance;
        const decimals = Number(response?.metadata[wallet.jetton]?.token_info[0]?.extra?.decimals);

        wallet.decimals = decimals;
        wallet.jetton_price_usd = getPrice;
        wallet.balance_price_usd = (getPrice * balance) / (10 ** decimals);
    });

    return response;
};

export const getTransactionTraceV3 = async function getEventsByAccount(hash, msge) {
    const queryParam = msge ? `msg_hash=${hash}` : `tx_hash=${hash}`;
    let response = await http.get(`traces?${queryParam}`, {
        params: {
            include_actions: true,
            supported_action_types,
        },
        paramsSerializer: { indexes: null },
    });

    if (response?.data?.traces?.length === 0 && !msge) {
        response = await http.get(`traces?msg_hash=${hash}`, {
            params: {
                include_actions: true,
                supported_action_types,
            },
        });
    }

    let pending = false;

    // Means this is pending transaction
    if (!response?.data?.traces[0]) {
        pending = true;

        response = await http.get('/pendingTraces', {
            params: {
                ext_msg_hash: hash,
            },
        });
    }

    const { trace: rootTrace, transactions } = response.data.traces[0];
    const addressBook = response.data.address_book;

    const warning = response.data.traces[0]?.warning === 'trace is too large';

    const tx_count = transactions ? Object.keys(transactions)?.length : 0;
    let commited_tx_count = 0;

    if (!warning) {
        Object.values(transactions).forEach((tx) => {
            if (!tx.emulated) {
                commited_tx_count += 1;
            }
        });
    }

    let progress = 0;

    if (tx_count > 0) {
        // Use this if statement to prevent dividing by zero (tx_count might be zero)
        progress = ((commited_tx_count / tx_count) * 100).toFixed();
    }

    function getUserFriendlyAddress(addr) {
        return addressBook[addr]?.user_friendly || addr;
    }

    let currentId = 1;
    const connections = [];

    function findTransactionByHash(tx_hash) {
        return transactions[tx_hash];
    }

    function processTransactionFields(transaction) {
        return {
            ...transaction,
            account: getUserFriendlyAddress(transaction?.account),
            in_msg: {
                ...transaction.in_msg,
                source: transaction.in_msg?.source ? getUserFriendlyAddress(transaction.in_msg.source) : null,
                destination: transaction.in_msg?.destination ? getUserFriendlyAddress(transaction.in_msg.destination) : null,
            },
            out_msgs: transaction.out_msgs?.map(msg => ({
                ...msg,
                source: msg.source ? getUserFriendlyAddress(msg.source) : null,
                destination: msg.destination ? getUserFriendlyAddress(msg.destination) : null,
            })) || [],
        };
    }

    function countAllDescendants(node) {
        if (!node.children || node.children.length === 0) {
            return 0;
        }
        return node.children.reduce((sum, child) => sum + countAllDescendants(child) + 1, 0);
    }

    function sortChildrenCentered(children) {
        if (children.length <= 1) return children;

        const sorted = [...children].sort((a, b) => a.childrenCount - b.childrenCount);

        const middleIndex = Math.floor((sorted.length - 1) / 2);
        const middleElement = sorted.shift();
        const finalSorted = [...sorted.slice(0, middleIndex), middleElement, ...sorted.slice(middleIndex)];

        return finalSorted;
    }

    function getLabelForOpCode(opCode) {
        if (opCode === null) {
            return 'Transfer';
        }

        return opCodesDictionary[opCode] || opCode;
    }

    function processNode(node, parentName = null) {
        let transaction = findTransactionByHash(node.tx_hash);
        transaction = processTransactionFields(transaction);

        const canonize = getUserFriendlyAddress(transaction.account);
        const name = `${canonize.slice(0, 2)}...${canonize.slice(-4)}`;
        const id = currentId;
        currentId += 1;
        const children = node.children ? Object.values(node.children).map(child => processNode(child, name)) : [];

        if (parentName) {
            connections.push({
                source: parentName,
                target: name,
                label: 'Oops',
                value: transaction.in_msg.value,
            });
        }

        const childrenWithCount = children.map(child => ({
            ...child,
            childrenCount: countAllDescendants(child),
        }));

        const sortedChildren = sortChildrenCentered(childrenWithCount);

        return {
            id,
            name,
            status: transaction.in_msg?.decoded_opcode === 'jetton_notify' || (transaction.description?.compute_ph?.success && !transaction.description?.aborted),
            transaction,
            interfaces: 'jetton',
            address: getUserFriendlyAddress(transaction.account),
            label: getLabelForOpCode(transaction.in_msg.opcode),
            value: transaction.in_msg.value,
            opCode: transaction.in_msg.opcode,
            children: sortedChildren,
        };
    }

    const trace = warning ? {} : processNode(rootTrace);

    const series = {
        traceId: response.data.traces[0].trace_id,
        timeStart: response.data.traces[0].start_utime,
    };

    const actions = [];

    if (warning) {
        response.data.traces[0].actions = actions;
        return {
            events: response.data.traces[0],
            trace,
            connections,
            series,
            duration: (response.data.traces[0].end_utime - response.data.traces[0].start_utime) || 5,
            pending,
            progress,
            warning,
        };
    }

    response.data.traces[0].actions.forEach((action) => {
        const msg = generateMessage(action, response.data.address_book, hash, response.data.metadata);
        if (msg.event === 'dex_deposit_liquidity' || msg.event === 'dex_withdraw_liquidity') {
            // special exception, we add another row
            actions.push({
                ...action,
                simple_preview: {
                    ...msg,
                    meta: {
                        symbol: msg.details.symbol_1,
                        jetton_address: msg.details.jetton_1_address,
                        decimals: msg.details.decimals_1,
                        amount: msg.details.amount_1,
                    },
                    action: {
                        type: 'dex:liquidity_asset_1',
                    },
                },
            });
            actions.push({
                ...action,
                simple_preview: {
                    ...msg,
                    meta: {
                        symbol: msg.details.symbol_2,
                        jetton_address: msg.details.jetton_2_address,
                        decimals: msg.details.decimals_2,
                        amount: msg.details.amount_2,
                    },
                    action: {
                        type: 'dex:liquidity_asset_2',
                    },
                },
            });
        } else {
            actions.push({
                ...action,
                simple_preview: msg,
            });
        }
    });

    response.data.traces[0].actions = actions;

    return {
        events: response.data.traces[0],
        trace,
        connections,
        series,
        duration: (response.data.traces[0].end_utime - response.data.traces[0].start_utime) || 5,
        pending,
        progress,
    };
};

export const getJettonInfo = async (address) => {
    try {
        const response = await http.get('jetton/masters', { params: { address } });
        return response.data;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};

export const getJettonHolders = async function (jetton_address, offset = 0, limit = 12) {
    const { data } = await http.get('jetton/wallets', { params: {
        jetton_address,
        offset,
        limit,
        sort: 'desc',
        exclude_zero_balance: true,
    } });

    return data;
};

export const getMultisig = async function (address) {
    const { data } = await http.get('multisig/wallets', { params: {
        address,
        limit: 10,
        offset: 0,
        sort: 'desc',
    } });

    return data;
};

export const getMultisigOrder = async function (address) {
    const { data } = await http.get('multisig/orders', { params: {
        address,
        limit: 10,
        offset: 0,
        sort: 'desc',
    } });

    return data;
};

export const getVesting = async function (address) {
    const { data } = await http.get('vesting', { params: {
        contract_address: address,
        limit: 10,
        offset: 0,
        sort: 'desc',
    } });

    return data;
};

export const getDomainByAddress = async function (address) {
    const { data } = await http.get('addressBook', { params: { address } });

    return data[address].domain;
};
